import { ChangeDetectorRef, EventEmitter, Injectable } from '@angular/core';
import { SeraTableLoader } from '../../ui/sera-table/models/SeraTableLoader';
import { Attachment } from 'src/app/analysis/models/attachment/Attachment';
import { MM13OpInstruction } from 'src/app/analysis/cr13/models/MM13OpInstruction';
import { AttachmentType } from 'src/app/analysis/models/attachment/AttachmentType';
import { Classification } from 'src/app/analysis/models/criterions/Classification';
import { Store } from '@ngrx/store';
import { displayToastError } from 'src/app/core/state/core.actions';
import { updateHazardAttachments, updateOPInstructions } from 'src/app/analysis/cr13/state/cr13.actions';
import { getFile, updateAnalysisAttachments, updateCrAttachments, updateForceCaseAttachments } from 'src/app/analysis/state/analysis.actions';
import { Subscription } from 'rxjs';
import { AnalysisService } from 'src/app/analysis/analysis.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AttachmentLevel } from 'src/app/analysis/models/attachment/AttachmentLevel';
import { SeraAttachmentsMapperService } from './sera-attachments-mapper.service';
import { NavigationUtils } from 'src/app/shared/utils/navigation-utils';

@Injectable({
    providedIn: 'root'
})
export class SeraAttachmentsLogicService {
    uploadingFile: boolean = false;
    uploadingFileSub?: Subscription;

    constructor(private store: Store, private analysisService: AnalysisService, private attachmentsMapperService: SeraAttachmentsMapperService) {
        this.uploadingFileSub = new Subscription();
    }

    handleAttachments(attachments: Attachment[] | undefined | MM13OpInstruction[], tableContent: SeraTableLoader, attachmentLevel: AttachmentLevel) {
        const attachmentsList: Attachment[] = [];
        const opInstructions: MM13OpInstruction[] = [];
        switch (attachmentLevel) {
            case AttachmentLevel.ForceCase:
            case AttachmentLevel.Analysis:
            case AttachmentLevel.CR1:
            case AttachmentLevel.CR2:
            case AttachmentLevel.CR3:
            case AttachmentLevel.CR4:
            case AttachmentLevel.CR5:
            case AttachmentLevel.CR6:
            case AttachmentLevel.CR7:
            case AttachmentLevel.CR8:
            case AttachmentLevel.CR9:
            case AttachmentLevel.CR10:
            case AttachmentLevel.CR11:
            case AttachmentLevel.CR12:
            case AttachmentLevel.CR13:
            case AttachmentLevel.HAZARD:
                attachments?.forEach(attachment => attachmentsList.push(this.attachmentsMapperService.mapAttachmentToTableEntry(attachment as Attachment)));
                tableContent.data = attachmentsList;
                break;
            case AttachmentLevel.OperatingInstruction:
                attachments?.forEach(opInstruction =>
                    opInstructions.push(this.attachmentsMapperService.mapOpInstructionToTableEntry(opInstruction as MM13OpInstruction))
                );
                tableContent.data = opInstructions;
                break;
        }
        return tableContent;
    }

    addAttachment(tableContent: SeraTableLoader, attachmentLevel: AttachmentLevel) {
        switch (attachmentLevel) {
            case AttachmentLevel.ForceCase:
            case AttachmentLevel.Analysis:
            case AttachmentLevel.CR1:
            case AttachmentLevel.CR2:
            case AttachmentLevel.CR3:
            case AttachmentLevel.CR4:
            case AttachmentLevel.CR5:
            case AttachmentLevel.CR6:
            case AttachmentLevel.CR7:
            case AttachmentLevel.CR8:
            case AttachmentLevel.CR9:
            case AttachmentLevel.CR10:
            case AttachmentLevel.CR11:
            case AttachmentLevel.CR12:
            case AttachmentLevel.CR13:
            case AttachmentLevel.HAZARD:
                this.addNormalAttachment(tableContent);
                break;
            case AttachmentLevel.OperatingInstruction:
                this.addOpInstruction(tableContent);
                break;
        }
    }

    addOpInstruction(tableContent: SeraTableLoader) {
        const newAttachment: Attachment = { attachmentType: AttachmentType.UPLOAD } as Attachment;
        const newOpInstruction: MM13OpInstruction = {
            description: '',
            attachment: newAttachment,
            instructionType: 'FILE',
            classification: Classification.REGULATION,
            isUploading: false
        } as MM13OpInstruction;
        tableContent.data = [newOpInstruction, ...tableContent.data];
    }

    addNormalAttachment(tableContent: SeraTableLoader) {
        const newAttachment: Attachment = {
            attachmentType: AttachmentType.UPLOAD,
            classification: Classification.REGULATION,
            isUploading: false
        } as Attachment;
        tableContent.data = [newAttachment, ...tableContent.data];
    }

    onSubmit(
        tableContent: SeraTableLoader,
        attachmentLevel: AttachmentLevel,
        toastErrorLabel: string,
        popUpEvent: EventEmitter<boolean>,
        criterionToChange?: string | null
    ) {
        switch (attachmentLevel) {
            case AttachmentLevel.ForceCase:
            case AttachmentLevel.Analysis:
            case AttachmentLevel.CR1:
            case AttachmentLevel.CR2:
            case AttachmentLevel.CR3:
            case AttachmentLevel.CR4:
            case AttachmentLevel.CR5:
            case AttachmentLevel.CR6:
            case AttachmentLevel.CR7:
            case AttachmentLevel.CR8:
            case AttachmentLevel.CR9:
            case AttachmentLevel.CR10:
            case AttachmentLevel.CR11:
            case AttachmentLevel.CR12:
            case AttachmentLevel.CR13:
            case AttachmentLevel.HAZARD:
                this.onSubmitOnNormalAttachments(tableContent, toastErrorLabel, popUpEvent, attachmentLevel, criterionToChange);
                break;
            case AttachmentLevel.OperatingInstruction:
                this.onSubmitOpInstruction(tableContent, toastErrorLabel, popUpEvent);
                break;
        }
    }

    onSubmitOnNormalAttachments(
        tableContent: SeraTableLoader,
        toastErrorLabel: string,
        popUpEvent: EventEmitter<boolean>,
        attachmentLevel: AttachmentLevel,
        criterionToChange?: string | null
    ) {
        if (this.checkIfRowsEmptyForNormalAttachments(tableContent)) {
            this.dispatchErrorToast(toastErrorLabel);
            return;
        }
        switch (attachmentLevel) {
            case AttachmentLevel.ForceCase:
                this.store.dispatch(updateForceCaseAttachments({ attachments: [...tableContent.data], criterionToChange: criterionToChange! }));
                break;
            case AttachmentLevel.Analysis:
                this.store.dispatch(updateAnalysisAttachments({ analysisAttachments: [...tableContent.data] }));
                break;
            case AttachmentLevel.HAZARD:
                this.store.dispatch(updateHazardAttachments({ attachments: [...tableContent.data] }));
                break;
            case AttachmentLevel.CR1:
                this.store.dispatch(updateCrAttachments({ attachments: [...tableContent.data], criterionToChange: 'dataMM01' }));
                break;
            case AttachmentLevel.CR2:
                this.store.dispatch(updateCrAttachments({ attachments: [...tableContent.data], criterionToChange: 'dataMM02' }));
                break;
            case AttachmentLevel.CR3:
                this.store.dispatch(updateCrAttachments({ attachments: [...tableContent.data], criterionToChange: 'dataMM03' }));
                break;
            case AttachmentLevel.CR4:
                this.store.dispatch(updateCrAttachments({ attachments: [...tableContent.data], criterionToChange: 'dataMM04' }));
                break;
            case AttachmentLevel.CR5:
                this.store.dispatch(updateCrAttachments({ attachments: [...tableContent.data], criterionToChange: 'dataMM05' }));
                break;
            case AttachmentLevel.CR6:
                this.store.dispatch(updateCrAttachments({ attachments: [...tableContent.data], criterionToChange: 'dataMM06' }));
                break;
            case AttachmentLevel.CR7:
                this.store.dispatch(updateCrAttachments({ attachments: [...tableContent.data], criterionToChange: 'dataMM07' }));
                break;
            case AttachmentLevel.CR8:
                this.store.dispatch(updateCrAttachments({ attachments: [...tableContent.data], criterionToChange: 'dataMM08' }));
                break;
            case AttachmentLevel.CR9:
                this.store.dispatch(updateCrAttachments({ attachments: [...tableContent.data], criterionToChange: 'dataMM09' }));
                break;
            case AttachmentLevel.CR10:
                this.store.dispatch(updateCrAttachments({ attachments: [...tableContent.data], criterionToChange: 'dataMM10' }));
                break;
            case AttachmentLevel.CR11:
                this.store.dispatch(updateCrAttachments({ attachments: [...tableContent.data], criterionToChange: 'dataMM11' }));
                break;
            case AttachmentLevel.CR12:
                this.store.dispatch(updateCrAttachments({ attachments: [...tableContent.data], criterionToChange: 'dataMM12' }));
                break;
            case AttachmentLevel.CR13:
                this.store.dispatch(updateCrAttachments({ attachments: [...tableContent.data], criterionToChange: 'dataMM13' }));
                break;
        }
        popUpEvent.emit();
    }

    checkIfRowsEmptyForNormalAttachments(tableContent: SeraTableLoader) {
        return tableContent.data.some(attachment => {
            if (!attachment.description) {
                return true;
            } else if (attachment.attachmentType === AttachmentType.UPLOAD && !attachment.filename) {
                return true;
            } else return attachment.attachmentType === AttachmentType.URL && !attachment.url;
        });
    }

    onSubmitOpInstruction(tableContent: SeraTableLoader, toastErrorLabel: string, popUpEvent: EventEmitter<boolean>) {
        if (this.checkIfOpRowsEmpty(tableContent)) {
            this.dispatchErrorToast(toastErrorLabel);
            return;
        }
        this.store.dispatch(updateOPInstructions({ opInstruction: [...tableContent.data] }));
        popUpEvent.emit();
    }

    checkIfOpRowsEmpty(tableContent: SeraTableLoader) {
        return tableContent.data.some(opInstruction => {
            if (!opInstruction.description) {
                return true;
            } else if (opInstruction.instructionType === AttachmentType.FILE && !opInstruction.attachment.filename) {
                return true;
            } else return opInstruction.instructionType === AttachmentType.URL && !opInstruction.attachment.url;
        });
    }

    dispatchErrorToast(toastErrorLabel: string) {
        this.store.dispatch(displayToastError({ message: toastErrorLabel }));
    }

    getLinkOrFileFromNormalAttachments(data: { rowData: Attachment; rowIndex: number }) {
        if (data.rowData.attachmentType === AttachmentType.URL && data.rowData.url) {
            NavigationUtils.openUrlInNewTab(data.rowData.url);
        }
        if (data.rowData.attachmentType === AttachmentType.UPLOAD && data.rowData.filename) {
            this.store.dispatch(
                getFile({
                    pathContext: data.rowData.id ? data.rowData.id + '/-/' + data.rowData.filename : '-1/' + data.rowData.tmpUuid + '/' + data.rowData.filename,
                    filename: data.rowData.filename
                })
            );
        }
    }

    getLinkOrFileForOpInstructions(data: { rowData: MM13OpInstruction; rowIndex: number }) {
        if (data.rowData.instructionType === AttachmentType.URL && data.rowData.attachment.url) {
            NavigationUtils.openUrlInNewTab(data.rowData.attachment.url);
        }
        if (data.rowData.instructionType === AttachmentType.FILE && data.rowData.attachment.filename) {
            this.store.dispatch(
                getFile({
                    pathContext: data.rowData.attachment.id
                        ? data.rowData.attachment.id + '/-/' + data.rowData.attachment.filename
                        : '-1/' + data.rowData.attachment.tmpUuid + '/' + data.rowData.attachment.filename,
                    filename: data.rowData.attachment.filename
                })
            );
        }
    }

    handleFileInputForNormalAttachments(cdr: ChangeDetectorRef, event: any, rowData: Attachment) {
        if (event.target?.files[0]) {
            const selectedFile: File = event.target?.files[0];
            this.uploadingFile = true;
            rowData.isUploading = true;
            const formData = new FormData();
            formData.append('file', selectedFile);
            this.uploadingFileSub = this.analysisService.uploadFile(formData).subscribe({
                next: resp => {
                    this.uploadingFile = false;
                    rowData.isUploading = false;
                    rowData.filename = selectedFile.name;
                    rowData.tmpUuid = resp.data;
                    cdr.detectChanges();
                },

                error: (error: HttpErrorResponse) => {
                    this.uploadingFile = false;
                    rowData.isUploading = false;
                    cdr.detectChanges();
                    this.dispatchErrorToast(error.error.errCode ? 'ERROR.' + error.error.errCode + '.TXT' : 'ERROR.APP_FILE_UPLOAD.TXT');
                }
            });
        }
    }

    handleFileInputForOpInstruction(cdr: ChangeDetectorRef, event: any, rowData: MM13OpInstruction) {
        if (event.target?.files[0]) {
            this.uploadingFile = true;
            rowData.isUploading = true;
            const selectedFile: File = event.target?.files[0];
            const formData = new FormData();
            formData.append('file', selectedFile);
            this.uploadingFileSub = this.analysisService.uploadFile(formData).subscribe({
                next: resp => {
                    this.uploadingFile = false;
                    rowData.isUploading = false;
                    rowData.attachment.filename = selectedFile.name;
                    rowData.attachment.tmpUuid = resp.data;
                    cdr.detectChanges();
                },

                error: () => {
                    this.uploadingFile = false;
                    rowData.isUploading = false;
                    cdr.detectChanges();
                    this.dispatchErrorToast('ERROR.APP_FILE_UPLOAD.TXT');
                }
            });
        }
    }

    unsubscribeOfSubscription() {
        this.uploadingFile = false;
        this.uploadingFileSub?.unsubscribe();
    }
}
